import { Navigate, useLocation } from "react-router-dom";
import AppLoadingScreen from "@/components/AppLoadingScreen";
import { useAuth } from "@/contexts/AuthContext";
import { hasVpcApiKey } from "@/utils/vpcMode";

/**
 * Route guard for pages that any signed-in user may see, but anonymous
 * visitors (including live-demo visitors) may not.
 * - Not authenticated → /login, carrying the requested path as ?returnTo so
 *   login lands them where they were headed. Deep links into this page (a
 *   shared /whats-new#<release> link, say) are its main entry point, so
 *   dropping the destination would strip the link of its purpose.
 * - Auth still loading → spinner
 *
 * Narrower than <AdminRoute>, which additionally requires admin_mode.
 */
export default function SignedInRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <AppLoadingScreen />;
  }

  // VPC mode: an api_key stands in for a Firebase session; the backend still
  // enforces authz. Mirrors the same escape hatch in AdminRoute.
  if (hasVpcApiKey()) {
    return <>{children}</>;
  }

  if (!user) {
    // URLSearchParams encodes the value: an unescaped "#" would truncate the
    // query string and cost the caller their anchor.
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?${new URLSearchParams({ returnTo })}`} replace />;
  }

  return <>{children}</>;
}
